/**
 * SystemDocumentsSection — P0 SPRINT (Feb 2026) · PERMANENT SHIPMENT ACCESS
 *
 * Toont "systeem-documenten" die ALTIJD beschikbaar zijn voor een zending
 * (Warehouse Receipt is er ALTIJD op basis van shipment-data — hoeft niet
 * expliciet gekoppeld te worden aan de junction collection).
 *
 * Deze sectie verschijnt bovenaan de Documenten-tab zodat medewerkers
 * (bijv. voor de Tamira Walle case) de Warehouse Receipt kunnen previewen
 * en opnieuw versturen zonder dat een documentrecord gekoppeld hoeft te zijn.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { FileText, Eye, Send } from "lucide-react";

import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";

import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

export default function SystemDocumentsSection({ shipment, onOpenReceiptPreview }) {
  const { hasPermission } = useAuth();
  const canSend = hasPermission("shipments:edit") || hasPermission("quotes:edit");

  const [resendOpen, setResendOpen] = useState(false);
  const [toEmail, setToEmail] = useState((shipment?.recipient?.email) || "");
  const [subject, setSubject] = useState(`Warehouse Receipt — ${shipment?.tracking_number || ""}`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const { runTask } = useGlobalLoading();

  const submitResend = async () => {
    if (!toEmail.trim()) {
      toast.error("E-mailadres is verplicht.");
      return;
    }
    setBusy(true);
    try {
      const res = await runTask({
        id: `resend-wr-${shipment.id}`,
        message: "Warehouse Receipt wordt verzonden…",
        run: () => adminApi.post(
          `/admin/shipping-labels/${shipment.id}/warehouse-receipt/resend`,
          {
            to_email: toEmail.trim(),
            subject: subject.trim(),
            message: message.trim() || undefined,
          },
        ),
      });
      toast.success(`Warehouse Receipt verzonden naar ${res.data.to}`);
      setResendOpen(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Verzenden mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3"
         data-testid="shipment-system-docs">
      <div className="text-[10px] uppercase font-bold text-blue-800 tracking-wide mb-2">
        Systeem-documenten (altijd beschikbaar)
      </div>
      <div className="flex items-center gap-2 flex-wrap bg-white border border-blue-100 rounded p-2">
        <FileText className="w-4 h-4 text-blue-700" />
        <div className="flex-1 min-w-[140px]">
          <div className="text-xs font-bold text-[#0A192F]">Warehouse Receipt</div>
          <div className="text-[10px] text-gray-500">
            Automatisch gegenereerd uit zendingdata — W-nummer {shipment?.tracking_number}
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-none text-xs"
                onClick={() => onOpenReceiptPreview?.()}
                data-testid="system-doc-wr-preview">
          <Eye className="w-3.5 h-3.5 mr-1" /> Preview / verzenden
        </Button>
        {canSend && (
          <Button size="sm" className="rounded-none text-xs bg-[#0055FF] text-white hover:bg-[#0044CC]"
                  onClick={() => {
                    setToEmail((shipment?.recipient?.email) || "");
                    setSubject(`Warehouse Receipt — ${shipment?.tracking_number || ""}`);
                    setResendOpen(true);
                  }}
                  data-testid="system-doc-wr-resend">
            <Send className="w-3.5 h-3.5 mr-1" /> Opnieuw versturen
          </Button>
        )}
      </div>

      {/* Resend dialog */}
      <Dialog open={resendOpen} onOpenChange={(v) => !busy && setResendOpen(v)}>
        <DialogContent className="max-w-lg" data-testid="wr-resend-dialog">
          <DialogHeader>
            <DialogTitle>Warehouse Receipt opnieuw versturen</DialogTitle>
            <DialogDescription>
              Dezelfde inhoud, geen nieuwe versie. Verzending wordt gelogd in het
              Communicatiecentrum.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Naar e-mailadres *</Label>
              <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                     data-testid="wr-resend-to-email" />
            </div>
            <div>
              <Label className="text-xs">Onderwerp</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                     data-testid="wr-resend-subject" />
            </div>
            <div>
              <Label className="text-xs">Bericht (HTML optioneel)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        placeholder="Leeg = standaard NL bericht met W-nummer"
                        data-testid="wr-resend-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendOpen(false)} disabled={busy}>Annuleren</Button>
            <Button onClick={submitResend} disabled={busy}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    data-testid="wr-resend-submit">
              Verstuur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
